import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn, Section } from './Section';
import { CheckIcon } from 'lucide-react';
const parts = [{
  numeral: 'I',
  title: 'La Base del Glow Up Financiero',
  subtitle: 'El punto de partida que cambia todo',
  desc: 'Antes de monetizar, necesitás entender desde dónde estás operando. Trabajás la relación que tenés con el dinero desde la raíz — creencias heredadas, patrones inconscientes y la identidad que te pusiste sin elegirla.',
  bullets: ['Identificá las creencias que te pusieron un techo invisible', 'Ejercicios de reprogramación con resultados en 7 días', 'La base mental de una mujer que cobra lo que vale']
}, {
  numeral: 'II',
  title: 'Tu Independencia Empieza Aquí',
  subtitle: 'De depender de otros a generar ingresos propios',
  desc: 'Convertís tu habilidad en una oferta real. Aprendés a identificar qué vale dinero, cómo presentarlo, cómo ponerle precio y cómo conseguir tus primeras clientas sin audiencia ni experiencia previa.',
  bullets: ['Definí tu oferta monetizable en menos de una hora', 'Calculá y comunicá tu precio sin bajar el número', 'Plan de acción para tus primeros ingresos en 30 días']
}, {
  numeral: 'III',
  title: 'Orden, Disciplina y Estabilidad',
  subtitle: 'El sistema que hace los ingresos constantes',
  desc: 'Generar dinero una vez no es lo mismo que tener estabilidad. Construís el sistema que sostiene tus ingresos mes a mes: hábitos, estructura financiera y disciplina sostenida.',
  bullets: ['Organizá ingresos, gastos y metas con claridad', 'Hábitos de mujeres financieramente estables', 'Sistema de disciplina que mantiene el crecimiento']
}, {
  numeral: 'IV',
  title: 'Expansión, Estándares y Vida',
  subtitle: 'La versión de vos que no acepta menos',
  desc: 'La parte final es la más poderosa. Hablamos de estándares de vida, de los entornos que te expanden o te reducen y de cómo convertirte en la mujer que diseña su vida.',
  bullets: ['Elevá tus estándares en todas las áreas sin culpa', 'El entorno que necesitás para no volver atrás', 'La identidad final: expansiva, próspera, libre']
}];
export function FourParts() {
  const [active, setActive] = useState(0);
  const current = parts[active];
  return <Section bg="gold-tint" data-id="element-125">
      <FadeIn className="text-center mb-8 md:mb-14" data-id="element-126">
        <h2 className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-gold mb-3 md:mb-4" data-id="element-127">
          Contenido del Ebook
        </h2>
        <p className="font-serif text-3xl md:text-5xl text-plum mb-3 md:mb-4" data-id="element-128">
          Las <span className="italic text-gold" data-id="element-129">4 Partes</span>
        </p>
        <p className="text-sm md:text-base text-plum/65 font-serif italic max-w-xl mx-auto" data-id="element-130">
          Tocá un número para explorar cada parte del método
        </p>
      </FadeIn>

      <div className="max-w-4xl mx-auto" data-id="element-131">
        {/* Roman numeral tabs */}
        <div className="grid grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8" data-id="element-132">
          {parts.map((part, i) => <button key={i} onClick={() => setActive(i)} className={`relative p-3 md:p-5 rounded-md text-center transition-all duration-300 ${active === i ? 'bg-plum text-gold shadow-xl shadow-plum/20' : 'bg-cream text-plum/70 hover:bg-cream-dark border border-plum/10'}`} data-id="element-133">
              <div className={`font-serif font-bold text-2xl md:text-4xl leading-none mb-1 ${active === i ? 'text-gold' : 'text-plum/40'}`} data-id="element-134">
                {part.numeral}
              </div>
              <div className={`text-[8px] md:text-[10px] uppercase tracking-widest font-bold ${active === i ? 'text-cream/80' : 'text-plum/50'}`} data-id="element-135">
                Parte
              </div>
              {active === i && <motion.div layoutId="active-part-indicator" className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-plum" data-id="element-136" />}
            </button>)}
        </div>

        {/* Active part content */}
        <AnimatePresence mode="wait" data-id="element-137">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -15
        }} transition={{
          duration: 0.35,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="bg-cream rounded-md p-5 md:p-10 border border-gold/20 shadow-xl shadow-plum/5" data-id="element-138">
            <span className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-gold mb-2 md:mb-3 block" data-id="element-139">
              Parte {current.numeral}
            </span>
            <h3 className="font-serif text-xl md:text-3xl text-plum mb-1 md:mb-2 leading-tight" data-id="element-140">
              {current.title}
            </h3>
            <p className="text-xs md:text-sm text-plum/55 italic font-serif mb-4 md:mb-5" data-id="element-141">
              {current.subtitle}
            </p>
            <p className="text-sm md:text-base text-plum/80 leading-relaxed mb-5 md:mb-6" data-id="element-142">
              {current.desc}
            </p>
            <ul className="space-y-2 md:space-y-3 pt-4 md:pt-5 border-t border-gold/15" data-id="element-143">
              {current.bullets.map((bullet, j) => <motion.li key={j} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.1 + j * 0.08
            }} className="flex items-start gap-2.5 md:gap-3" data-id="element-144">
                  <CheckIcon className="text-gold flex-shrink-0 mt-0.5 md:mt-1" size={16} data-id="element-145" />
                  <span className="text-sm md:text-base text-plum/85" data-id="element-146">
                    {bullet}
                  </span>
                </motion.li>)}
            </ul>
          </motion.div>
        </AnimatePresence>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-6 md:mt-8" data-id="element-147">
          {parts.map((_, i) => <button key={i} onClick={() => setActive(i)} className={`h-1 transition-all duration-300 rounded-full ${active === i ? 'w-8 bg-gold' : 'w-4 bg-plum/15 hover:bg-plum/30'}`} aria-label={`Ir a parte ${parts[i].numeral}`} data-id="element-148" />)}
        </div>
      </div>
    </Section>;
}